import React, { useState, useEffect, useMemo } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
  Tooltip,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

import { initializeApp } from "firebase/app";
import { getFirestore, collection, addDoc, doc, getDoc, updateDoc } from "firebase/firestore";

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyB4cv324tdhhitqFwjuDip1zq_qVZQrPPk",
  authDomain: "website-de-ba2fa.firebaseapp.com",
  projectId: "website-de-ba2fa",
  storageBucket: "website-de-ba2fa.firebasestorage.app",
  messagingSenderId: "48912058426",
  appId: "1:48912058426:web:9f12330302ac539c844d03"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: "#f5f5f5",
      paper: "#f5f5f5",
    },
    text: {
      primary: "#202124",
    },
    primary: {
      main: "#202124",
    },
  },
  typography: {
    fontFamily: "sans-serif",
  },
});

const translations = {
  de: {
    emailLabel: "E-Mail",
    description: `Newgate entwickelt ein umfassendes System zur datengetriebenen Analyse der Kapitalmärkte mit Fokus auf digitale Assets. Durch die Nutzung von Informationen aus zahlreichen Online-Quellen und den Einsatz moderner Machine-Learning-Technologien ist es möglich tiefgehende Einblicke für fortschrittliches Risikomanagement und intelligentere Investitionsentscheidungen zu gewinnen. Weitere Informationen finden Sie unter:`,
    successMessage: "Danke! Bitte bestätigen Sie Ihre E-Mail-Adresse über den Link, den wir Ihnen gesendet haben.",
    errorMessage: "Etwas ist schief gelaufen. Bitte versuchen Sie es erneut.",
    loading: "Wird geladen...",
    imprintTitle: "Impressum",
    imprintContent: `Newgate GmbH
            Steiningerweg 1
            85748 Garching

            Email: `,
    close: "Schließen"
  },
  en: {
    emailLabel: "Email",
    description: `Newgate is developing a comprehensive system for the data-centric analysis of capital markets with a focus on digital assets. By using information directly from numerous online sources and leveraging modern machine learning technology, Newgate unravels deep insights, leading to advanced risk management and smarter investments. For more information, visit:`,
    successMessage: "Thank you! Please confirm your email address using the link we sent you.",
    errorMessage: "Something went wrong. Please try again.",
    loading: "Loading...",
    imprintTitle: "Imprint",
    imprintContent: `Newgate GmbH
            Steiningerweg 1
            85748 Garching

            Email: `,
    close: "Close"
  },
};

function App() {
  const [language, setLanguage] = useState("de");

  const localPart = 'contact';       // left side vor @
  const domain = 'newgate.de';      // domain
  const email = `${localPart}@${domain}`;

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("success");

  // Add these state variables at the top of your component
  const [imprintOpen, setImprintOpen] = useState(false);

  const t = translations[language];

  const toggleLanguage = () => {
    const newLanguage = language === "de" ? "en" : "de";
    setLanguage(newLanguage);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box sx={{ padding: "16px", minHeight: "100vh" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "2rem",
          }}
        >
          <Button
            onClick={toggleLanguage}
            variant="outlined"
            sx={{
              marginLeft: "auto",
              minWidth: "32px",
              height: "32px",
              fontSize: "0.75rem",
              padding: "4px",
              color: "#000000",
              borderColor: "#000000",
              "&:hover": {
                backgroundColor: "#000000",
                color: "#f5f5f5",
              },
            }}
          >
            {language.toUpperCase()}
          </Button>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            minHeight: "60vh",
            gap: "1.5rem",
          }}
        >

          <a href="/">
            <img
              src="/logo.png"
              alt="Newgate"
              width="300"
              style={{ cursor: "pointer" }}
            />
          </a>
          <Typography
            variant="body1"
            sx={{
              maxWidth: "720px",
              fontSize: "1rem",
              lineHeight: 1.6,
              color: "#000000",
              whiteSpace: "pre-line",
            }}
          >
            {t.description}
            {" "}
            <a
              href="https://newgate.li"
              target="_self"
              rel="noopener noreferrer"
              style={{ color: "#000000", textDecoration: "underline" }}
            >
              newgate.li
            </a>
          </Typography>
        </Box>


        <Snackbar
          open={open}
          autoHideDuration={4000}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
        >
          <MuiAlert
            onClose={handleClose}
            severity={severity}
            elevation={6}
            variant="filled"
            sx={{
              width: "100%",
              backgroundColor:
                severity === "success"
                  ? "#202124"
                  : undefined,
              color:
                severity === "success"
                  ? "#f5f5f5"
                  : undefined,
            }}
          >
            {message}
          </MuiAlert>
        </Snackbar>

        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            color: '#000000',
            opacity: 0.6,
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
            background: '#f5f5f5',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.8rem',
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
              '&:hover': { opacity: 1 },
            }}
            onClick={() => setImprintOpen(true)}
          >
            {t.imprintTitle}
          </Typography>
        </Box>

        <Dialog open={imprintOpen} onClose={() => setImprintOpen(false)} maxWidth="sm" fullWidth slotProps={{
          paper: {
            sx: {
              backgroundColor: '#f5f5f5',
              backgroundImage: 'none',
            }
          }
        }}>
          <DialogTitle>{t.imprintTitle}</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {t.imprintContent} {email}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImprintOpen(false)}>{t.close}</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider >
  );
}

export default App;